import React, { useState } from 'react';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  Button,
  CardHeader,
  Divider,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { PlusIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { FormattedMessage } from 'react-intl';
import messages from './messages';
import HUDContractRow from './HUDContractRow';
import DeleteContractModal from './DeleteContractModal';
import { DASHES } from '@fortress-technology-solutions/fortress-component-library/constants';
import { useHUDSubsidyTypeSectionHandlers } from './HUDSubsidyTypeSection.hooks';

const HUDSubsidyTypeSection = ({
  editMode,
  hudPrimarySubsidyType,
  hudSecondarySubsidyType,
  intl,
  onChange,
  primaryContractValidations = [],
  primaryContracts = [],
  secondaryContractValidations = [],
  secondaryContracts = [],
  showMinimumRent = false,
}) => {
  const {
    eligibilityUniverseCodeOptions,
    handleAddPrimaryContract,
    handlePrimaryContractChange,
    handleRemovePrimaryContract,
    handleAddSecondaryContract,
    handleSecondaryContractChange,
    handleRemoveSecondaryContract,
  } = useHUDSubsidyTypeSectionHandlers({
    intl,
    onChange,
    primaryContracts,
    secondaryContracts,
  });

  const [pendingRemoval, setPendingRemoval] = useState(null);

  // Removing a contract unassigns every unit on it, so confirm first — but only when there is
  // something to lose. An unsaved contract has no id and no units, and `assignedUnitCount` is
  // absent until the backend supplies it, so both fall through to the immediate removal.
  //
  const resolveRemovalHandler = (isPrimary) =>
    isPrimary ? handleRemovePrimaryContract : handleRemoveSecondaryContract;

  // Not flag-gated: the cascade this warns about runs alongside the contract's own soft delete.
  const requestRemoval = (index, isPrimary) => {
    const contracts = isPrimary ? primaryContracts : secondaryContracts;
    const contract = contracts[index];

    if (contract?.id && contract?.assignedUnitCount > 0) {
      setPendingRemoval({ index, isPrimary });
      return;
    }

    resolveRemovalHandler(isPrimary)(index);
  };

  const handleRemovalCancel = () => setPendingRemoval(null);

  const handleRemovalConfirm = () => {
    if (!pendingRemoval) {
      return;
    }
    const { index, isPrimary } = pendingRemoval;
    setPendingRemoval(null);
    resolveRemovalHandler(isPrimary)(index);
  };

  return (
    <>
      {/* Primary Subsidy Type */}
      <Grid item xs={12}>
        <CardHeader
          title={<FormattedMessage {...messages.hudSubsidyType} />}
          subheader={hudPrimarySubsidyType?.name || DASHES}
          subheaderTypographyProps={{
            variant: 'bodyMedium',
            color: 'text.primary',
          }}
          sx={{ paddingLeft: 0, paddingBottom: 0 }}
          action={
            editMode && hudPrimarySubsidyType ? (
              <Button
                variant="text"
                size="small"
                startIcon={<PlusIcon />}
                onClick={handleAddPrimaryContract}
              >
                <FormattedMessage {...messages.addContract} />
              </Button>
            ) : null
          }
        />
      </Grid>
      {/* Primary Contracts */}
      {hudPrimarySubsidyType &&
        primaryContracts.map((contract, index) => (
          <HUDContractRow
            key={contract.id ?? `primary-${index}`}
            contract={contract}
            index={index}
            editMode={editMode}
            canRemove={
              editMode && (Boolean(contract?.id) || primaryContracts.length > 1)
            }
            eligibilityUniverseCodeOptions={eligibilityUniverseCodeOptions}
            intl={intl}
            isExpirationBeforeEffective={
              primaryContractValidations[index] ?? false
            }
            onContractChange={(updated) =>
              handlePrimaryContractChange(index, updated)
            }
            onRemove={() => requestRemoval(index, true)}
            primarySubsidyTypeCode={hudPrimarySubsidyType.code}
            showMinimumRent={showMinimumRent}
          />
        ))}

      <Grid item xs={12}>
        <Divider />
      </Grid>

      {/* Secondary Subsidy Type */}
      <Grid item xs={12}>
        <CardHeader
          title={<FormattedMessage {...messages.hudSecondarySubsidyType} />}
          subheader={
            typeof hudSecondarySubsidyType === 'string'
              ? hudSecondarySubsidyType
              : hudSecondarySubsidyType?.name || DASHES
          }
          subheaderTypographyProps={{
            variant: 'bodyMedium',
            color: 'text.primary',
          }}
          sx={{ paddingLeft: 0, paddingBottom: 0 }}
          action={
            editMode && hudSecondarySubsidyType ? (
              <Button
                variant="text"
                size="small"
                startIcon={<PlusIcon />}
                onClick={handleAddSecondaryContract}
              >
                <FormattedMessage {...messages.addContract} />
              </Button>
            ) : null
          }
        />
      </Grid>

      {/* Secondary Contracts */}
      {hudSecondarySubsidyType &&
        secondaryContracts.map((contract, index) => (
          <HUDContractRow
            key={contract.id ?? `secondary-${index}`}
            contract={contract}
            index={index}
            editMode={editMode}
            canEdit={!!hudSecondarySubsidyType}
            canRemove={
              editMode &&
              (Boolean(contract?.id) || secondaryContracts.length > 1)
            }
            eligibilityUniverseCodeOptions={eligibilityUniverseCodeOptions}
            intl={intl}
            isExpirationBeforeEffective={
              secondaryContractValidations[index] ?? false
            }
            onContractChange={(updated) =>
              handleSecondaryContractChange(index, updated)
            }
            onRemove={() => requestRemoval(index, false)}
            showMinimumRent={showMinimumRent}
          />
        ))}

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <DeleteContractModal
        open={Boolean(pendingRemoval)}
        onClose={handleRemovalCancel}
        onConfirm={handleRemovalConfirm}
        intl={intl}
      />
    </>
  );
};

export default HUDSubsidyTypeSection;
